import { useState, useEffect } from "react";
import { Bell, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePublicWaiterCall } from "@/hooks/useWaiterCalls";
import { toast } from "sonner";

interface CallWaiterButtonProps {
  restaurantId: string;
  tableNumber: string;
}

export function CallWaiterButton({ restaurantId, tableNumber }: CallWaiterButtonProps) {
  const { callWaiter } = usePublicWaiterCall();
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [justCalled, setJustCalled] = useState(false);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleCallWaiter = async () => {
    if (cooldown > 0 || isLoading) return;

    setIsLoading(true);

    const success = await callWaiter(restaurantId, tableNumber);

    setIsLoading(false);

    if (success) {
      setJustCalled(true);
      setCooldown(30);
      toast.success("Garçom chamado!", {
        description: "Um garçom virá até sua mesa em breve.",
      });

      // Reset "just called" state after 3 seconds
      setTimeout(() => setJustCalled(false), 3000);
    } else {
      toast.error("Erro ao chamar garçom", {
        description: "Tente novamente em alguns segundos.",
      });
    }
  };

  return (
    <Button
      onClick={handleCallWaiter}
      disabled={isLoading || cooldown > 0}
      variant={justCalled ? "default" : "outline"}
      className={`gap-2 transition-all ${
        justCalled
          ? "bg-success hover:bg-success text-success-foreground"
          : cooldown > 0
          ? "opacity-70"
          : ""
      }`}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : justCalled ? (
        <Check className="h-4 w-4" />
      ) : (
        <Bell className="h-4 w-4" />
      )}
      {isLoading
        ? "Chamando..."
        : justCalled
        ? "Garçom chamado!"
        : cooldown > 0
        ? `Aguarde ${cooldown}s`
        : "Chamar Garçom"}
    </Button>
  );
}
